import { useState, useEffect } from 'react';
import { useAppSelector } from '../../store/hooks';
import './AutoApproval.css';
import { CarService } from '../../services/car.service';
import type { ICar } from '../../services/car.service';

const AutoApproval = () => {
  const [filteredCars, setFilteredCars] = useState<ICar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Состояние для фильтров поиска
  const [searchFilters, setSearchFilters] = useState({
    carNumber: '',
    carMark: '',
    autoparkName: '',
  });

  const user = useAppSelector((state) => state.user.user);

  // Функция для загрузки данных из API через сервис
  const fetchCars = async () => {
    try {
      setLoading(true);

      // Формируем параметры запроса
      const params: Record<string, any> = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Добавляем техника, если он есть
      if (user && user.id) {
        params.technikID = user.id;
      }

      // Добавляем фильтры поиска, если они заполнены
      if (searchFilters.carNumber) {
        params.number = searchFilters.carNumber;
      }

      if (searchFilters.carMark) {
        params.mark = searchFilters.carMark;
      }

      if (searchFilters.autoparkName) {
        params.autoparkName = searchFilters.autoparkName;
      }

      // Используем сервис для получения автомобилей с автосогласованием
      const response = await CarService.getCarsWithAutoNegotiation(params);

      setFilteredCars(response.data);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setLoading(false);
    } catch (err) {
      setError('Ошибка при загрузке данных');
      setLoading(false);
      console.error('Error fetching cars with autonegotiation:', err);
    }
  };

  // Загружаем данные при монтировании компонента или изменении параметров
  useEffect(() => {
    fetchCars();
  }, [currentPage, itemsPerPage, user?.id]);

  // Обработчик изменения полей поиска
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearchFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Функция для применения фильтров
  const applyFilters = () => {
    setCurrentPage(1); // Сбрасываем на первую страницу при применении фильтров
    fetchCars();
  };

  // Функция для удаления автомобиля из списка автосогласования
  const handleRemoveCar = async (carId: string) => {
    try {
      // Используем сервис для удаления автосогласования
      await CarService.removeAutoNegotiation(carId);

      // Обновляем список после успешного удаления
      fetchCars();
      alert('Автомобиль успешно удален из списка автосогласования');
    } catch (err) {
      setError('Ошибка при удалении автомобиля');
      console.error('Error removing car from auto approval:', err);
    }
  };

  // Функция сброса фильтров
  const resetFilters = () => {
    setSearchFilters({
      carNumber: '',
      carMark: '',
      autoparkName: '',
    });
    setCurrentPage(1);
    fetchCars();
  };

  // Обработчик изменения страницы
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="auto-approval">
      <div className="auto-approval-filters">
        <div className="auto-approval-filter-row">
          <div className="auto-approval-filter-item">
            <label>Гос. номер</label>
            <input
              type="text"
              name="carNumber"
              value={searchFilters.carNumber}
              onChange={handleSearchChange}
              placeholder="Введите гос. номер"
            />
          </div>
          <div className="auto-approval-filter-item">
            <label>Марка авто</label>
            <input
              type="text"
              name="carMark"
              value={searchFilters.carMark}
              onChange={handleSearchChange}
              placeholder="Введите марку авто"
            />
          </div>
          <div className="auto-approval-filter-item">
            <label>Автопарк</label>
            <input
              type="text"
              name="autoparkName"
              value={searchFilters.autoparkName}
              onChange={handleSearchChange}
              placeholder="Введите название автопарка"
            />
          </div>
          <div className="auto-approval-filter-item">
            <button className="apply-filters-btn" onClick={applyFilters}>
              Применить
            </button>
            <button className="reset-filters-btn" onClick={resetFilters}>
              Сбросить
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-indicator">Загрузка...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          <div className="auto-approval-stats">
            <span>Всего автомобилей с автосогласованием: {totalCount}</span>
          </div>

          {filteredCars.length > 0 ? (
            <>
              <table className="auto-approval-table">
                <thead>
                  <tr>
                    <th>Гос. номер</th>
                    <th>Марка/Модель</th>
                    <th>Автопарк</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCars.map((car) => (
                    <tr key={car.id}>
                      <td>{car.number}</td>
                      <td>
                        {car.mark} {car.model}
                      </td>
                      <td>{car.autopark?.legalName || 'Не указан'}</td>
                      <td>
                        <button className="remove-car-btn" onClick={() => handleRemoveCar(car.id)}>
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Пагинация */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    &lt; Назад
                  </button>

                  <span className="pagination-info">
                    Страница {currentPage} из {totalPages}
                  </span>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Вперед &gt;
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-cars-message">Автомобилей с автосогласованием не найдено</div>
          )}
        </>
      )}
    </div>
  );
};

export default AutoApproval;
